import React, { useState } from 'react';
import authService from '../services/authService';
import { formatError } from '../utils/errorHelper';
import { MdPerson, MdLock, MdSave, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import './Profile.css';

function Profile() {
  const user = authService.getCurrentUser();
  
  const [nameFormData, setNameFormData] = useState({
    name: user?.name || '',
  });
  
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  
  const [loadingName, setLoadingName] = useState(false);
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [nameSuccess, setNameSuccess] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [nameError, setNameError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleNameChange = (e) => {
    setNameFormData({
      ...nameFormData,
      [e.target.name]: e.target.value,
    });
    setNameError('');
    setNameSuccess('');
  };

  const handlePasswordChange = (e) => {
    setPasswordFormData({
      ...passwordFormData,
      [e.target.name]: e.target.value,
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    setLoadingName(true);
    setNameError('');
    setNameSuccess('');

    try {
      if (nameFormData.name.trim().length < 3) {
        setNameError('O nome deve ter pelo menos 3 caracteres');
        setLoadingName(false);
        return;
      }

      await authService.updateProfile(nameFormData.name.trim());
      setNameSuccess('Nome atualizado com sucesso!');
    } catch (error) {
      setNameError(formatError(error, 'Erro ao atualizar nome. Tente novamente.'));
    } finally {
      setLoadingName(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoadingPassword(true);
    setPasswordError('');
    setPasswordSuccess('');

    try {
      if (passwordFormData.newPassword.length < 8) {
        setPasswordError('A nova senha deve ter pelo menos 8 caracteres');
        setLoadingPassword(false);
        return;
      }

      if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
        setPasswordError('As senhas não coincidem');
        setLoadingPassword(false);
        return;
      }

      await authService.changePassword(
        passwordFormData.currentPassword,
        passwordFormData.newPassword,
        passwordFormData.confirmPassword
      );
      
      setPasswordSuccess('Senha alterada com sucesso!');
      setPasswordFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      setPasswordError(formatError(error, 'Erro ao alterar senha. Verifique sua senha atual.'));
    } finally {
      setLoadingPassword(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  return (
    <div className="profile-page">
      <div className="page-header">
        <h1>Meu Perfil</h1>
      </div>

      <div className="profile-container">
        {/* Card de informações do usuário */}
        <div className="profile-info-card">
          <div className="profile-avatar">
            <div className="avatar-circle">
              {user?.name?.[0]?.toUpperCase() || 'U'}
            </div>
          </div>
          <div className="profile-info">
            <h2>{user?.name || 'Usuário'}</h2>
            <p className="profile-email">{user?.email || ''}</p>
          </div>
        </div>

        {/* Formulário de atualizar nome */}
        <div className="profile-section">
          <div className="section-header">
            <MdPerson className="section-icon" />
            <h3>Atualizar Nome</h3>
          </div>
          <form onSubmit={handleNameSubmit} className="profile-form">
            <div className="form-group">
              <label htmlFor="name">Nome</label>
              <input
                type="text"
                id="name"
                name="name"
                value={nameFormData.name}
                onChange={handleNameChange}
                required
                placeholder="Digite seu nome"
                disabled={loadingName}
              />
            </div>

            {nameSuccess && <div className="success-message">{nameSuccess}</div>}
            {nameError && <div className="error-message">{nameError}</div>}

            <button
              type="submit"
              className="btn-primary"
              disabled={loadingName}
            >
              <MdSave />
              {loadingName ? 'Salvando...' : 'Salvar Nome'}
            </button>
          </form>
        </div>

        {/* Formulário de alterar senha */}
        <div className="profile-section">
          <div className="section-header">
            <MdLock className="section-icon" />
            <h3>Alterar Senha</h3>
          </div>
          <form onSubmit={handlePasswordSubmit} className="profile-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Senha Atual</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.current ? 'text' : 'password'}
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordFormData.currentPassword}
                  onChange={handlePasswordChange}
                  required
                  placeholder="Digite sua senha atual"
                  disabled={loadingPassword}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => togglePasswordVisibility('current')}
                  tabIndex="-1"
                >
                  {showPasswords.current ? <MdVisibilityOff /> : <MdVisibility />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">Nova Senha</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.new ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={passwordFormData.newPassword}
                  onChange={handlePasswordChange}
                  required
                  placeholder="Digite sua nova senha (mínimo 8 caracteres)"
                  disabled={loadingPassword}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => togglePasswordVisibility('new')}
                  tabIndex="-1"
                >
                  {showPasswords.new ? <MdVisibilityOff /> : <MdVisibility />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar Nova Senha</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.confirm ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordFormData.confirmPassword}
                  onChange={handlePasswordChange}
                  required
                  placeholder="Confirme sua nova senha"
                  disabled={loadingPassword}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => togglePasswordVisibility('confirm')}
                  tabIndex="-1"
                >
                  {showPasswords.confirm ? <MdVisibilityOff /> : <MdVisibility />}
                </button>
              </div>
            </div>

            {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}
            {passwordError && <div className="error-message">{passwordError}</div>}

            <button
              type="submit"
              className="btn-primary btn-password"
              disabled={loadingPassword}
            >
              <MdLock />
              {loadingPassword ? 'Alterando...' : 'Alterar Senha'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Profile;
